import { VStack } from '@chakra-ui/react';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useJournalAnalytics } from 'app/analytics/useJournalAnalytics';
import { dateUtils } from 'shared_DEPRECATED/utils';

import {
  JournalAddNoteFormChallengesSection,
  JournalAddNoteFormPostToFeedSection,
  JournalAddNoteFormAttachMediaSection,
  JournalAddNoteFormTextSection,
  JournalAddNoteDialogFooter,
  JournalAddNoteDialogHeader,
  JOURNAL_NOTE_DEFAULT_VALUE,
  JournalNotePOSTType,
  useJournalAddNoteMutation,
} from 'features/journal';

import { ModalBody, ModalHeader, ModalFooter } from 'shared/components/Modal';
import { showErrorToast } from 'shared/components/Toast';
import { Typography } from 'shared/components/Typography';
import { useUploadFiles } from 'shared/hooks/useFileUpload';

type TJournalNoteAddDialog = {
  participantId: string;
  challengeId?: string;
  challengeStatusDate?: string;
  onAddNoteSuccess?: () => void;
  customJournalPrompt?: string;
};

export const JournalNoteAddDialog = ({
  challengeId,
  challengeStatusDate = dateUtils(new Date()).format('YYYY-MM-DD'),
  onAddNoteSuccess,
  participantId,
  customJournalPrompt,
}: TJournalNoteAddDialog) => {
  const methods = useForm<JournalNotePOSTType>({
    defaultValues: {
      ...JOURNAL_NOTE_DEFAULT_VALUE,
      challengeStatusDate,
      ...(challengeId && {
        challengeId,
      }),
    },
  });

  const { mutateAsync: addNote } = useJournalAddNoteMutation({
    onSuccess: onAddNoteSuccess,
    participantId,
  });

  const { uploadFiles } = useUploadFiles();

  const onSubmit = useCallback(
    async (note: JournalNotePOSTType) => {
      try {
        let mediaIds: string[] = [];

        if (Boolean(note.media.length)) {
          mediaIds = await uploadFiles(note.media);
        }

        await addNote({
          ...note,
          mediaIds,
        });
      } catch (err) {
        showErrorToast(
          "There's an error with creating the note. Please try again later."
        );
      }
    },
    [addNote, uploadFiles]
  );

  const { sendNoteAddedAnalyticsEvent } = useJournalAnalytics();

  const onSubmitButtonClick = methods.handleSubmit(
    (note: JournalNotePOSTType) => {
      onSubmit(note);

      sendNoteAddedAnalyticsEvent();
    }
  );

  return (
    <>
      <ModalHeader>
        <JournalAddNoteDialogHeader />
      </ModalHeader>
      <FormProvider {...methods}>
        <ModalBody>
          <VStack width="100%" alignItems="flex-start">
            {customJournalPrompt && (
              <Typography color="black" fontWeight="medium" type="heading">
                {customJournalPrompt}
              </Typography>
            )}
            <JournalAddNoteFormTextSection />
            <JournalAddNoteFormChallengesSection />
            <JournalAddNoteFormAttachMediaSection />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <JournalAddNoteFormPostToFeedSection />
          <JournalAddNoteDialogFooter onSubmit={onSubmitButtonClick} />
        </ModalFooter>
      </FormProvider>
    </>
  );
};
